import styled, { css, keyframes } from 'styled-components';

/* ------------------------------------------------------------------ */
/* Page shell                                                          */
/* ------------------------------------------------------------------ */

export const DashboardWrapper = styled.div`
  width: 100%;
  min-height: calc(100vh - 64px);
  background: ${({ theme }) => theme.landing.background};
  transition: background 0.3s ease;
`;

export const DashboardContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  margin-top: 64px;
  padding: ${({ theme }) => theme.landingSpacing.xl};

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.lg}) {
    padding: ${({ theme }) => theme.landingSpacing.lg};
  }

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.md}) {
    padding: ${({ theme }) => theme.landingSpacing.md};
  }

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    padding: ${({ theme }) => theme.landingSpacing.md};
  }
`;

/* ------------------------------------------------------------------ */
/* Page header (title / status / description + client code pill)      */
/* ------------------------------------------------------------------ */

export const PageHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.landingSpacing.lg};
  margin-bottom: ${({ theme }) => theme.landingSpacing.xxl};

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.md}) {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: ${({ theme }) => theme.landingSpacing.xl};
  }
`;

export const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.landingSpacing.sm};
  flex-wrap: wrap;
`;

export const Title = styled.h1`
  font-weight: 700;
  font-size: 1.75rem;
  line-height: 1.2;
  letter-spacing: -0.02em;
  margin: 0;
  color: ${({ theme }) => theme.landing.text};

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    font-size: 1.5rem;
  }
`;

export const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.landingSpacing.xs};
  padding: 2px ${({ theme }) => theme.landingSpacing.sm};
  border-radius: ${({ theme }) => theme.landingBorderRadius.sm};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #16a34a;
  background: rgba(34, 197, 94, 0.12);
  border: 1px solid rgba(34, 197, 94, 0.28);
`;

export const StatusDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #22c55e;
  flex-shrink: 0;
`;

export const Description = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.normal};
  line-height: 1.5;
  color: ${({ theme }) => theme.landing.textSecondary};
  margin: 4px 0 0 0;
`;

/* ------------------------------------------------------------------ */
/* Client code pill                                                    */
/* ------------------------------------------------------------------ */

export const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.landingSpacing.sm};

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.md}) {
    width: 100%;
  }
`;

export const ClientCodePill = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.landingSpacing.sm};
  background: ${({ theme }) => theme.landing.cardBg};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  padding: ${({ theme }) => theme.landingSpacing.sm} ${({ theme }) => theme.landingSpacing.md};
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    width: 100%;
    justify-content: space-between;
    flex-wrap: wrap;
    row-gap: ${({ theme }) => theme.landingSpacing.xs};
  }
`;

export const ClientCodeLabel = styled.span`
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.landing.text};
  white-space: nowrap;
`;

export const ClientCodeValue = styled.span`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: ${({ theme }) => theme.landing.text};
  background: ${({ theme }) => theme.landing.iconBg};
  padding: 2px ${({ theme }) => theme.landingSpacing.sm};
  border-radius: ${({ theme }) => theme.landingBorderRadius.sm};
`;

export const PillDivider = styled.div`
  width: 1px;
  height: 14px;
  background: ${({ theme }) => theme.landing.cardBorder};
  flex-shrink: 0;
`;

export const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: ${({ theme }) => theme.landingBorderRadius.sm};
  background: transparent;
  color: ${({ theme }) => theme.landing.textSecondary};
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease,
    transform 0.1s ease;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    background: ${({ theme }) => theme.landing.iconBg};
    color: ${({ theme }) => theme.landing.text};
  }

  &:active {
    transform: scale(0.94);
  }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

export const RegenerateButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.landingSpacing.xs};
  background: transparent;
  border: none;
  padding: 0;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.landing.linkColor};
  font-family: 'JetBrains Mono Regular';
  cursor: pointer;
  white-space: nowrap;
  transition:
    color 0.15s ease,
    transform 0.1s ease;

  svg {
    width: 13px;
    height: 13px;
  }

  &:hover {
    color: ${({ theme }) => theme.landing.linkHover};
  }

  &:active {
    transform: scale(0.97);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  svg[data-spin='true'] {
    animation: spin 1s linear infinite;
  }
`;

/* ------------------------------------------------------------------ */
/* Metric cards                                                        */
/* ------------------------------------------------------------------ */

export const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${({ theme }) => theme.landingSpacing.lg};

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.lg}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: ${({ theme }) => theme.landingSpacing.md};
  }

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

export const MetricCard = styled.div`
  background: ${({ theme }) => theme.landing.cardBg};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  border-radius: ${({ theme }) => theme.landingBorderRadius.xl};
  padding: ${({ theme }) => theme.landingSpacing.lg};
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  transition:
    border-color 0.15s ease,
    transform 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.landing.buttonBg};
    box-shadow: 0 8px 24px rgba(37, 99, 235, 0.08);
    transform: translateY(-2px);
    cursor: pointer;
  }

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    padding: ${({ theme }) => theme.landingSpacing.md};
  }
`;

export const MetricHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.landingSpacing.sm};
`;

export const MetricLabelGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.landingSpacing.sm};
`;

/* Small icon chip before the label (same theme colours) */
export const MetricIcon = styled.span`
  display: inline-grid;
  place-items: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  background: ${({ theme }) => theme.landing.iconBg};
  color: ${({ theme }) => theme.landing.text};

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const MetricLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.normal};
  font-weight: 500;
  color: ${({ theme }) => theme.landing.text};
`;

const badgeVariants = {
  success: css`
    color: #16a34a;
    background: rgba(34, 197, 94, 0.12);
    border-color: rgba(34, 197, 94, 0.24);
  `,
  danger: css`
    color: #e11d48;
    background: rgba(244, 63, 94, 0.1);
    border-color: rgba(244, 63, 94, 0.22);
  `,
  info: css`
    color: #4f46e5;
    background: rgba(99, 102, 241, 0.12);
    border-color: rgba(99, 102, 241, 0.24);
  `,
};

export const MetricBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px ${({ theme }) => theme.landingSpacing.xs};
  border-radius: ${({ theme }) => theme.landingBorderRadius.sm};
  border: 1px solid;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;

  svg {
    width: 12px;
    height: 12px;
  }

  ${({ $variant }) => badgeVariants[$variant] || badgeVariants.info}
`;

export const MetricValueRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.landingSpacing.sm};
  margin-top: ${({ theme }) => theme.landingSpacing.md};
  flex-wrap: wrap;
`;

export const MetricValue = styled.span`
  font-weight: 700;
  font-size: 2.625rem;
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.landing.text};
  font-variant-numeric: tabular-nums;

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    font-size: 2.125rem;
  }
`;

export const MetricSubtitle = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.small};
  color: ${({ $accent, theme }) => ($accent ? '#16a34a' : theme.landing.textSecondary)};
  font-weight: ${({ $accent }) => ($accent ? 600 : 400)};
`;

export const MetricFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: ${({ theme }) => theme.landingSpacing.md};
  padding-top: ${({ theme }) => theme.landingSpacing.md};
  border-top: 1px solid ${({ theme }) => theme.landing.cardBorder};
`;

export const MetricFooterText = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.landing.textSecondary};
`;

export const MetricArrow = styled.span`
  display: inline-flex;
  color: ${({ theme }) => theme.landing.text};
  transition:
    color 0.15s ease,
    transform 0.15s ease;

  svg {
    width: 15px;
    height: 15px;
  }

  ${MetricCard}:hover & {
    color: ${({ theme }) => theme.landing.buttonBg};
    transform: translateX(2px);
  }
`;

const ping = keyframes`
  75%, 100% {
    transform: scale(1.7);
    opacity: 0;
  }
`;

export const PulseDot = styled.span`
  position: relative;
  display: inline-grid;
  place-items: center;
  flex-shrink: 0;
  min-width: 24px;
  height: 24px;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 999px;
    background: ${({ theme }) => theme.colors.statusActive};
    opacity: 0.55;
    animation: ${ping} 1.6s cubic-bezier(0, 0, 0.2, 1) infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    &::before {
      animation: none;
      opacity: 0;
    }
  }
`;

export const PulseCount = styled.span`
  position: relative;
  display: inline-grid;
  place-items: center;
  min-width: 24px;
  height: 24px;
  padding: 0 7px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.statusActive};
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  box-shadow: 0 0 0 2px ${({ theme }) => theme.landing.cardBg};
`;
